import React from 'react';
import { CheckCircle, Download, Loader } from 'lucide-react';
import { channelLabel } from '../utils/channelInventory';

/**
 * SendResultsModal
 *
 * Observational window into the staging-area send process. Renders two modes
 * driven by the live `sendResult` state from useStagingArea:
 *
 *   in-flight mode  : streaming row list as recipients are processed; banner
 *                     instructs the user not to close the tab; Stop sending
 *                     button cancels the loop after the in-flight recipient
 *                     completes; CSV download is hidden until the send finishes.
 *   complete mode   : final report with CSV download and Done.
 *
 * Styling uses the SAM Surveyor design tokens (UI artifacts/design-tokens.css)
 * via CSS variables: --color-*, --spacing-*, --radius-*, --font-*, --shadow-*.
 */
export default function SendResultsModal({ result, inProgress, onClose, onCancelSend }) {
    if (!result) return null;

    const surveys = Array.isArray(result.surveys) ? result.surveys : [];
    const sentAt = result.sentAt || new Date().toISOString();
    const totalOverall = Number(result.totalOverall || 0);
    const doneOverall = Number(result.doneOverall || 0);
    const cancelled = Boolean(result.cancelled);
    // Aggregate the outcomes still needed to choose the completed-state title
    // and display the WhatsApp provider-acceptance note.
    const totals = surveys.reduce(
        (acc, s) => {
            const rs = Array.isArray(s.recipients) ? s.recipients : [];
            for (const r of rs) {
                if (r.status === 'sent' || r.status === 'queued') acc.sent += 1;
                else if (r.status === 'failed') acc.failed += 1;
            }
            return acc;
        },
        { sent: 0, failed: 0 }
    );

    const downloadCsv = () => {
        const header = [
            'Survey', 'Deployment Slug', 'Recipient ID', 'Email', 'Name',
            'Channel', 'Status', 'URL', 'Error', 'Sent At',
        ];
        const rows = [header];
        for (const s of surveys) {
            const recs = Array.isArray(s.recipients) ? s.recipients : [];
            for (const r of recs) {
                rows.push([
                    s.surveyName || '',
                    s.deploymentSlug || '',
                    r.recipient_id || '',
                    r.email || '',
                    r.name || '',
                    r.channel || '',
                    r.status || '',
                    r.url || '',
                    r.error || s.error || '',
                    sentAt,
                ]);
            }
        }
        const csv = rows
            .map(row => row.map(v => `"${String(v).replace(/"/g, '""')}"`).join(','))
            .join('\n');
        const blob = new Blob([csv], { type: 'text/csv' });
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        const stamp = sentAt.replace(/[:T]/g, '-').replace(/\..+$/, '').slice(0, 16);
        a.download = `staging-send-${stamp}.csv`;
        a.click();
    };

    // Title reflects mode, and names the channel the batch is going out on
    // (ruled by Mike 2026-08-08) — a long-running Google Chat send should say
    // so while you watch it.
    const channelName = channelLabel(result.channel) || '';
    const via = channelName ? ` via ${channelName}` : '';
    const successful = !inProgress && !cancelled && totals.failed === 0;
    const successTitle = result.channel === 'channel_response'
        ? 'Successfully Sent via Channel Request Method'
        : `Successfully Sent via Direct Channel Method${channelName ? `: ${channelName}` : ''}`;
    const title = inProgress
        ? `Send in progress${via}`
        : cancelled
            ? `Send cancelled${via}`
            : successful
                ? successTitle
                : `Send results${via}`;

    return (
        <div style={styles.overlay} onClick={onClose}>
            <div style={styles.modal} onClick={e => e.stopPropagation()}>
                <h3 style={styles.title}>
                    <span>{title}</span>
                    {successful && (
                        <CheckCircle
                            size={20}
                            color="var(--color-success)"
                            strokeWidth={2}
                            aria-hidden="true"
                        />
                    )}
                </h3>

                {inProgress && (
                    <div style={styles.banner}>
                        <Loader size={14} strokeWidth={1.5} style={styles.bannerSpinner} />
                        <div>
                            <div style={styles.bannerLine}>
                                <strong>Do not close this tab.</strong> Closing this window is fine — the send continues in the background.
                            </div>
                            <div style={styles.bannerProgress}>
                                {doneOverall} / {totalOverall} processed
                                {result.currentRecipient && (
                                    <span style={styles.muted}>
                                        {' · sending to '}{result.currentRecipient.name || result.currentRecipient.email || result.currentRecipient.recipient_id}
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {result.channel === 'whatsapp' && totals.sent > 0 && (
                    <div style={styles.deliveryNote}>
                        Provider acceptance is not delivery. Delivery confirmations and failures appear in Channels Activity.
                    </div>
                )}

                <div style={styles.scrollArea}>
                    {surveys.map((s, si) => {
                        const recs = Array.isArray(s.recipients) ? s.recipients : [];
                        return (
                            <div key={s.deploymentId || si} style={styles.surveyBlock}>
                                <div style={styles.surveyHeader}>
                                    <div>
                                        <strong style={styles.surveyName}>{s.surveyName || s.deploymentSlug || s.deploymentId}</strong>
                                        {s.deploymentSlug && (
                                            <span style={styles.slug}>· {s.deploymentSlug}</span>
                                        )}
                                    </div>
                                    {s.ok === false && s.error && (
                                        <span style={styles.surveyError}>{s.error}</span>
                                    )}
                                </div>
                                <ul style={styles.recipientList}>
                                    {recs.map((r, ri) => (
                                        <li key={(r.recipient_id || '') + ri} style={styles.recipientRow}>
                                            <div style={styles.recipientLine}>
                                                <strong style={styles.recipientName}>{r.name || r.email || r.recipient_id}</strong>
                                                {r.email && r.email !== r.name && (
                                                    <span style={styles.muted}> · {r.email}</span>
                                                )}
                                                {r.channel && (
                                                    <span style={styles.muted}>
                                                        {' · via '}{r.channel}
                                                        {r.notification_channel ? ` (notified via ${r.notification_channel})` : ''}
                                                    </span>
                                                )}
                                            </div>
                                            {r.error && (
                                                <div style={styles.errorLine}>{r.error}</div>
                                            )}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        );
                    })}
                </div>

                <div style={styles.actions}>
                    {inProgress ? (
                        <>
                            <button
                                onClick={onCancelSend}
                                style={styles.dangerButton}
                                title="Stop sending — already-completed recipients keep their status; remaining recipients are marked cancelled."
                            >
                                Stop sending
                            </button>
                            <button onClick={onClose} style={{ ...styles.secondaryButton, marginLeft: 8 }}>
                                Hide monitor
                            </button>
                        </>
                    ) : (
                        <>
                            <button onClick={downloadCsv} style={styles.secondaryButton}>
                                <Download size={14} strokeWidth={1.5} style={styles.iconLeading} /> CSV
                            </button>
                            <button
                                onClick={onClose}
                                style={{ ...styles.successButton, marginLeft: 8 }}
                                autoFocus
                            >
                                Done
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

const styles = {
    overlay: {
        position: 'fixed', inset: 0, zIndex: 10000,
        background: 'rgba(0, 0, 0, 0.3)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontFamily: 'var(--font-family-primary)',
    },
    modal: {
        background: 'var(--color-bg-panel)',
        borderRadius: 'var(--radius-lg)',
        padding: 'var(--spacing-lg)',
        width: 'min(640px, calc(100vw - 32px))',
        maxHeight: 'calc(100vh - 64px)',
        display: 'flex', flexDirection: 'column',
        boxShadow: 'var(--shadow-xl)',
        color: 'var(--color-text-primary)',
    },
    title: {
        margin: '0 0 var(--spacing-md)',
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--spacing-sm)',
        fontSize: 'var(--font-size-md)',
        fontWeight: 'var(--font-weight-semibold)',
        color: 'var(--color-text-primary)',
    },
    banner: {
        display: 'flex', alignItems: 'flex-start', gap: 'var(--spacing-sm)',
        padding: 'var(--spacing-sm) var(--spacing-md)',
        background: 'var(--color-primary-50)',
        border: '1px solid var(--color-primary-100)',
        borderRadius: 'var(--radius-base)',
        fontSize: 'var(--font-size-sm)',
        color: 'var(--color-text-primary)',
        marginBottom: 'var(--spacing-md)',
    },
    bannerSpinner: {
        color: 'var(--color-primary-600)',
        flexShrink: 0,
        marginTop: 2,
    },
    deliveryNote: {
        margin: 'calc(-1 * var(--spacing-xs)) 0 var(--spacing-md)',
        fontSize: 'var(--font-size-xs)',
        color: 'var(--color-text-secondary)',
    },
    bannerLine: { lineHeight: 'var(--line-height-sm)' },
    bannerProgress: {
        marginTop: 2,
        color: 'var(--color-text-secondary)',
        fontSize: 'var(--font-size-xs)',
    },
    muted: {
        fontSize: 'var(--font-size-xs)',
        color: 'var(--color-text-secondary)',
    },
    scrollArea: {
        overflowY: 'auto',
        flex: 1,
        minHeight: 100,
        maxHeight: 'calc(100vh - 280px)',
        border: '1px solid var(--color-border-light)',
        borderRadius: 'var(--radius-base)',
        padding: '6px 4px',
        background: 'var(--color-bg-panel)',
    },
    surveyBlock: {
        marginBottom: 'var(--spacing-sm)',
        paddingBottom: 'var(--spacing-sm)',
        borderBottom: '1px solid var(--color-border-light)',
    },
    surveyHeader: {
        display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
        padding: '4px 8px',
        fontSize: 'var(--font-size-sm)',
    },
    surveyName: { color: 'var(--color-text-primary)' },
    slug: {
        fontSize: 'var(--font-size-xs)',
        color: 'var(--color-text-tertiary)',
        marginLeft: 6,
    },
    surveyError: {
        fontSize: 'var(--font-size-xs)',
        color: 'var(--color-error)',
    },
    recipientList: { listStyle: 'none', margin: 0, padding: '0 var(--spacing-sm)' },
    recipientRow: {
        padding: '4px 0',
        fontSize: 'var(--font-size-sm)',
    },
    recipientLine: { display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' },
    recipientName: { color: 'var(--color-text-primary)' },
    errorLine: {
        fontSize: 'var(--font-size-xs)',
        color: 'var(--color-error)',
        marginTop: 2,
    },
    actions: {
        display: 'flex', justifyContent: 'flex-end',
        marginTop: 'var(--spacing-md)',
    },
    secondaryButton: {
        padding: '6px 16px',
        border: '1px solid var(--color-border-medium)',
        borderRadius: 'var(--radius-base)',
        background: 'var(--color-bg-panel)',
        color: 'var(--color-text-primary)',
        cursor: 'pointer',
        fontSize: 'var(--font-size-sm)',
        fontFamily: 'var(--font-family-primary)',
    },
    successButton: {
        padding: '6px 16px',
        border: '1px solid var(--color-success)',
        borderRadius: 'var(--radius-base)',
        background: 'var(--color-success)',
        color: '#FFFFFF',
        cursor: 'pointer',
        fontSize: 'var(--font-size-sm)',
        fontWeight: 'var(--font-weight-medium)',
        fontFamily: 'var(--font-family-primary)',
    },
    dangerButton: {
        padding: '6px 16px',
        border: '1px solid var(--color-error)',
        borderRadius: 'var(--radius-base)',
        background: 'var(--color-bg-panel)',
        color: 'var(--color-error)',
        cursor: 'pointer',
        fontSize: 'var(--font-size-sm)',
        fontWeight: 'var(--font-weight-medium)',
        fontFamily: 'var(--font-family-primary)',
    },
    iconLeading: { marginRight: 4, verticalAlign: 'middle' },
};
